/**
 * Video API Client
 *
 * API Endpoints:
 * - GET /videos/{id}/play → Video
 * - GET /videos → VideoListResponse
 * - GET /public/videos/related → VideoListResponse
 * - POST /videos/{id}/toggle-like → LikeResponse
 * - POST /videos/{id}/toggle-dislike → DislikeResponse
 * - POST /watchlist/{video:uuid}/toggle → BookmarkResponse
 * - GET /watchlist → VideoListResponse (user's watchlist)
 * - GET /videos/{id}/comments-list → CommentListResponse
 * - POST /videos/{id}/comment → Comment
 */

import type {
  ApiResponse,
  BookmarkResponse,
  Comment,
  CommentListResponse,
  DislikeResponse,
  LikeResponse,
  Video,
  VideoListResponse,
} from '../types';
import { captureError } from '@/shared/lib/errors/capture';
import { apiClient } from './base-client';

export interface VideoListFilters {
  page?: number;
  per_page?: number;
  limit?: number;
  short?: boolean;
  is_short?: boolean;
  type?: string;
  types?: string;
  published?: boolean;
  order?: 'asc' | 'desc';
  channel_id?: number;
  creator_id?: number;
  tag_ids?: number[];
  country_id?: string[];
  sort_by?: 'trending' | 'newest' | 'oldest' | 'longest' | 'shortest';
}

export interface RelatedVideoFilters {
  video_id: string | number;
  page?: number;
  limit?: number;
  short?: boolean;
  is_short?: boolean;
  type?: string;
  published?: boolean;
  sort_by?: string;
  order?: 'asc' | 'desc';
}

const transformVideoResponse = (data: {
  videos?: Video[];
  pagination?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
  };
}): VideoListResponse => {
  const videos = (data.videos || []).map(
    (video: Video & { creator?: { channel?: { id: number; name: string } } }) => ({
      ...video,
      channel: video.channel || video.creator?.channel,
    })
  );

  return {
    data: videos,
    current_page: data.pagination?.current_page || 1,
    last_page: data.pagination?.last_page || 1,
    per_page: data.pagination?.per_page || 20,
    total: data.pagination?.total || 0,
    first_page_url: '',
    from: null,
    last_page_url: '',
    links: [],
    next_page_url: null,
    path: '',
    prev_page_url: null,
    to: null,
  };
};

export const videoClient = {
  /**
   * Play video (returns video + related videos)
   *
   * WARNING: GET /videos/{id}/play records a view as a side-effect on the backend.
   * Only call this when the user is actually on the video player page (/videos/[id]).
   * Do NOT use for metadata lookups, prefetch, link previews, or routing logic.
   *
   * TODO (backend): Add a read-only GET /videos/{id} endpoint that returns
   * metadata without tracking a view.
   */
  play: async (
    id: string | number,
    serverToken?: string
  ): Promise<{ video: Video; videos: Video[] }> => {
    const data = await apiClient.get<{ video?: Video; videos?: Video[]; data?: Video }>(
      `/videos/${id}/play`,
      serverToken ? { serverToken } : undefined
    );
    return {
      video: data.video || data.data || (data as Video),
      videos: data.videos || [],
    };
  },

  /**
   * Read-only metadata lookup. NO view-tracking side effect.
   * Behind VIDEO_SPLIT_ENDPOINTS_ENABLED. Replaces the metadata portion of /play.
   * Safe to cache cross-request; safe to prefetch.
   */
  getMetadata: async (uuid: string, serverToken?: string): Promise<Video | null> => {
    const data = await apiClient.get<{ video?: Video; data?: Video }>(
      `/videos/${uuid}/metadata`,
      serverToken ? { serverToken } : undefined
    );
    return data.video || data.data || (data as Video | null);
  },

  /**
   * Record one playback session. Single source of truth for view counts.
   * BE dedupes via Redis SETNX 30s. FE should call exactly once per session
   * after first `playing` event (NOT in prefetch path — see no-ghost-view rule).
   */
  recordView: async (
    uuid: string
  ): Promise<{ ok: boolean; watch_count: number; user_progress?: number }> => {
    return apiClient.post<{ ok: boolean; watch_count: number; user_progress?: number }>(
      `/videos/${uuid}/view`
    );
  },

  /**
   * Recommended videos for a watch page. BE preserves the legacy Laravel
   * paginator wrapper for shape parity with the old `/recommended`, so the
   * response is `{ videos: { data: Video[], current_page, ... } }`. Unwrap
   * here so callers get a flat `Video[]` — array-shape responses are still
   * accepted to keep the client tolerant if BE ever flattens.
   */
  getRecommended: async (uuid: string, serverToken?: string): Promise<Video[]> => {
    const data = await apiClient.get<{
      videos?: Video[] | { data?: Video[] };
      data?: Video[];
    }>(`/videos/${uuid}/recommended`, serverToken ? { serverToken } : undefined);
    if (Array.isArray(data.videos)) return data.videos;
    if (data.videos && Array.isArray(data.videos.data)) return data.videos.data;
    return data.data || [];
  },

  /**
   * Per-user state for one video. Streams in parallel with /metadata so the
   * watch page can SSR like/dislike/watchlist/progress without bundling them
   * into the cacheable metadata payload.
   */
  getMeVideoState: async (
    uuid: string,
    serverToken?: string
  ): Promise<{
    has_liked: boolean;
    has_disliked: boolean;
    in_watchlist: boolean;
    user_progress?: number;
    watch_count?: number;
  } | null> => {
    return apiClient.get<{
      has_liked: boolean;
      has_disliked: boolean;
      in_watchlist: boolean;
      user_progress?: number;
      watch_count?: number;
    }>(`/me/video-state/${uuid}`, serverToken ? { serverToken } : undefined);
  },

  /**
   * Get list of videos with filters
   */
  list: async (filters?: VideoListFilters, serverToken?: string): Promise<VideoListResponse> => {
    const params: Record<string, unknown> = {
      page: filters?.page,
      limit: filters?.limit || filters?.per_page || 24,
      short: filters?.short ?? false,
      is_short: filters?.is_short ?? false,
      type: filters?.type || 'video',
      published: filters?.published ?? true,
    };

    params.sort_by = filters?.sort_by || 'published_at';
    if (!filters?.sort_by) {
      params.order = filters?.order || 'desc';
    }

    // Add creator filter by channel_id
    if (filters?.channel_id !== undefined) {
      params.channel_id = filters.channel_id;
    }

    // Add creator filter by user_id (creator_id)
    if (filters?.creator_id !== undefined) {
      params.creator_id = filters.creator_id;
    }

    // Add tag filter - send as array
    if (filters?.tag_ids && filters.tag_ids.length > 0) {
      params.tag_ids = filters.tag_ids;
    }

    // Add country filter - send as array
    if (filters?.country_id && filters.country_id.length > 0) {
      params.country_id = filters.country_id;
    }

    const data = await apiClient.get<{
      videos?: Video[];
      pagination?: {
        current_page?: number;
        last_page?: number;
        per_page?: number;
        total?: number;
      };
    }>('/videos', { params, ...(serverToken ? { serverToken } : {}) });

    return transformVideoResponse(data);
  },

  /**
   * Get long-form videos only (excludes shorts)
   */
  getLongForm: async (page = 1, perPage = 24): Promise<VideoListResponse> => {
    const response = await videoClient.list({
      page,
      per_page: perPage,
      short: false,
      is_short: false,
      type: 'video',
      published: true,
    });

    // Defensive: filter out any shorts if backend ignores flags
    const filtered = response.data.filter(
      (v) => v?.short !== true && v?.is_short !== true && v?.type !== 'short'
    );

    if (process.env.NODE_ENV === 'development' && filtered.length !== response.data.length) {
      console.warn('Filtered out short content from /videos response');
    }

    return { ...response, data: filtered };
  },

  /**
   * Get related videos for a video
   */
  getRelated: async (
    videoId: string | number,
    page = 1,
    perPage = 12,
    serverToken?: string
  ): Promise<VideoListResponse> => {
    try {
      const data = await apiClient.get<{
        videos?: Video[];
        data?: Video[];
        pagination?: {
          current_page?: number;
          last_page?: number;
          total?: number;
        };
      }>('/public/videos/related', {
        params: {
          video_id: videoId,
          page,
          limit: perPage,
          short: false,
          is_short: false,
          type: 'video',
          published: true,
          sort_by: 'published_at',
          order: 'desc',
        },
        ...(serverToken ? { serverToken } : {}),
      });

      const list = data?.videos || data?.data || [];
      const filtered = list.filter(
        (v) => v?.id !== videoId && v?.short !== true && v?.is_short !== true && v?.type !== 'short'
      );

      return {
        data: filtered,
        current_page: data?.pagination?.current_page ?? page,
        last_page: data?.pagination?.last_page ?? page,
        per_page: perPage,
        total: data?.pagination?.total ?? filtered.length,
        first_page_url: '',
        from: null,
        last_page_url: '',
        links: [],
        next_page_url: null,
        path: '',
        prev_page_url: null,
        to: null,
      };
    } catch (error) {
      captureError(error, {
        component: 'VideoClient',
        action: 'toggle-autoplay',
        severity: 'warning',
      });
      // Fallback: use long-form list and filter out current video
      const fallback = await videoClient.getLongForm(page, perPage + 4);
      fallback.data = fallback.data.filter((v) => v?.id !== videoId);
      return fallback;
    }
  },

  /**
   * Toggle like on a video
   */
  toggleLike: async (id: string | number): Promise<LikeResponse> => {
    return apiClient.post<LikeResponse>(`/videos/${id}/toggle-like`);
  },

  /**
   * Toggle dislike on a video
   */
  toggleDislike: async (id: string | number): Promise<DislikeResponse> => {
    return apiClient.post<DislikeResponse>(`/videos/${id}/toggle-dislike`);
  },

  /**
   * Toggle watchlist (bookmark) on a video by UUID
   */
  toggleBookmark: async (videoUuid: string | number): Promise<BookmarkResponse> => {
    return apiClient.post<BookmarkResponse>(`/watchlist/${videoUuid}/toggle`);
  },

  /**
   * Get comments for a video
   */
  getComments: async (id: string | number, page = 1): Promise<CommentListResponse> => {
    const data = await apiClient.get<{ comments?: CommentListResponse }>(
      `/videos/${id}/comments-list`,
      {
        params: { page },
      }
    );
    return data.comments || (data as CommentListResponse);
  },

  addComment: async (id: string | number, content: string, parentId?: number): Promise<Comment> => {
    const data = await apiClient.post<ApiResponse<Comment>>(`/videos/${id}/comment`, {
      content,
      parent_id: parentId,
    });
    return data.data;
  },

  /**
   * Toggle autoplay preference
   */
  toggleAutoplay: async (): Promise<{ video_autoplay: boolean }> => {
    return apiClient.post<{ video_autoplay: boolean }>('/videos/toggle-autoplay');
  },

  /**
   * Get user's watchlist (bookmarked videos)
   */
  getBookmarked: async (page = 1, perPage = 12): Promise<VideoListResponse> => {
    const data = await apiClient.get<{
      watchlist?: Video[];
      videos?: Video[];
      data?: Video[];
      meta?: {
        current_page?: number;
        last_page?: number;
        per_page?: number;
        total?: number;
      };
      pagination?: {
        current_page?: number;
        last_page?: number;
        per_page?: number;
        total?: number;
      };
    }>('/watchlist', {
      params: { page, per_page: perPage } as Record<string, unknown>,
    });

    const videos = data.watchlist || data.videos || data.data || [];
    const paginationSource = data.meta || data.pagination;
    return {
      data: videos,
      current_page: paginationSource?.current_page ?? page,
      last_page: paginationSource?.last_page ?? 1,
      per_page: paginationSource?.per_page ?? perPage,
      total: paginationSource?.total ?? videos.length,
      first_page_url: '',
      from: null,
      last_page_url: '',
      links: [],
      next_page_url: null,
      path: '',
      prev_page_url: null,
      to: null,
    };
  },

  /**
   * Get watch history
   */
  getHistory: async (page = 1, perPage = 12): Promise<VideoListResponse> => {
    const data = await apiClient.get<{ videos?: VideoListResponse } | VideoListResponse>(
      '/profile/watch-history',
      {
        params: { page, per_page: perPage } as Record<string, unknown>,
      }
    );
    return typeof data === 'object' && data !== null && 'videos' in data
      ? data.videos || data
      : (data as VideoListResponse);
  },

  /**
   * Get liked videos
   */
  getLiked: async (page = 1, perPage = 12): Promise<VideoListResponse> => {
    const data = await apiClient.get<{ videos?: VideoListResponse } | VideoListResponse>(
      '/profile/liked-videos',
      {
        params: { page, per_page: perPage } as Record<string, unknown>,
      }
    );
    return typeof data === 'object' && data !== null && 'videos' in data
      ? data.videos || data
      : (data as VideoListResponse);
  },
};
